'use client';
import { Box, Typography, Button, AppBar, Toolbar, Container, Chip, IconButton, Menu, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { Menu as MenuIcon, TrendingUp } from '@mui/icons-material';
import { useState } from 'react';
import CompanyLogoFull from './CompanyLogoFull';

export default function TopNavigation() {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigationItems = [
    { label: 'About Us', href: '/about' },
    { label: 'Features', href: '/features' },
    { label: 'Challenges', href: '/challenges' },
    { label: 'Outcomes', href: '/outcomes' },
    { label: 'Time Savings', href: '/time-savings' },
    { label: 'Contact', href: '/contact' }
  ];

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{ 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderBottom: '1px solid rgba(21,60,127,0.1)',
        backdropFilter: 'blur(10px)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          px: { xs: 2, md: 0 },
          py: 1,
          minHeight: '80px'
        }}>
          {/* Left Section - Logo and App Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Link href="/" style={{ textDecoration: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Box sx={{ width: 200, height: 50, display: 'flex', alignItems: 'center' }}>
                  <CompanyLogoFull />
                </Box>
              </Box>
            </Link>
          </Box>
          
          {/* Center Section - Navigation Links (Desktop) */}
          <Box sx={{ 
            display: { xs: 'none', lg: 'flex' }, 
            gap: 1,
            alignItems: 'center'
          }}>
            {navigationItems.map((item, index) => (
              <Link key={index} href={item.href} style={{ textDecoration: 'none' }}>
                <Button
                  variant="text"
                  sx={{ 
                    color: '#333',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      color: '#153C7F',
                      bgcolor: 'rgba(21,60,127,0.08)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  {item.label}
                </Button>
              </Link>
            ))}
          </Box>
          
          {/* Right Section - Call to Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center'
          }}>
            {/* Desktop CTA Buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
              <Link href="/login" style={{ textDecoration: 'none' }}>
                <Button 
                  variant="outlined" 
                  size="medium"
                  sx={{ 
                    color: '#153C7F',
                    borderColor: 'rgba(21,60,127,0.3)',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#153C7F',
                      bgcolor: 'rgba(21,60,127,0.05)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Login
                </Button>
              </Link>
              
              <Link href="/signup" style={{ textDecoration: 'none' }}>
                <Button 
                  variant="outlined" 
                  size="medium"
                  sx={{ 
                    color: '#153C7F',
                    borderColor: 'rgba(21,60,127,0.3)',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#153C7F',
                      bgcolor: 'rgba(21,60,127,0.05)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  Register
                </Button>
              </Link>
              
              <Button 
                variant="contained" 
                size="medium"
                startIcon={<TrendingUp />}
                sx={{ 
                  background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                  color: 'white',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: '0 4px 15px rgba(21,60,127,0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #0F2A5A, #153C7F)',
                    boxShadow: '0 6px 20px rgba(21,60,127,0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Request Demo
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                color: '#153C7F',
                bgcolor: 'rgba(21,60,127,0.05)',
                '&:hover': {
                  bgcolor: 'rgba(21,60,127,0.1)'
                }
              }}
              onClick={handleMobileMenuOpen}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 250,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(21,60,127,0.1)',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {navigationItems.map((item, index) => (
          <MenuItem 
            key={index}
            onClick={handleMobileMenuClose}
            component={Link}
            href={item.href}
            sx={{
              py: 2,
              px: 3,
              '&:hover': {
                bgcolor: 'rgba(21,60,127,0.05)'
              }
            }}
          >
            <Typography sx={{ color: '#333', fontWeight: 600, fontSize: '0.95rem' }}>
              {item.label}
            </Typography>
          </MenuItem>
        ))}
        
        <Box sx={{ borderTop: '1px solid rgba(21,60,127,0.1)', mt: 1, pt: 1 }}>
          <MenuItem 
            onClick={handleMobileMenuClose}
            component={Link}
            href="/login"
            sx={{
              py: 2,
              px: 3,
              '&:hover': {
                bgcolor: 'rgba(21,60,127,0.05)'
              }
            }}
          >
            <Typography sx={{ color: '#153C7F', fontWeight: 600, fontSize: '0.95rem' }}>
              Login
            </Typography>
          </MenuItem>
          
          <MenuItem 
            onClick={handleMobileMenuClose}
            component={Link}
            href="/signup"
            sx={{
              py: 2,
              px: 3,
              '&:hover': {
                bgcolor: 'rgba(21,60,127,0.05)'
              }
            }}
          >
            <Typography sx={{ color: '#153C7F', fontWeight: 600, fontSize: '0.95rem' }}>
              Register
            </Typography>
          </MenuItem>
          
          <MenuItem 
            onClick={handleMobileMenuClose}
            sx={{
              py: 2,
              px: 3,
              '&:hover': {
                bgcolor: 'rgba(21,60,127,0.05)'
              }
            }}
          >
            <Typography sx={{ color: '#153C7F', fontWeight: 700, fontSize: '0.95rem' }}>
              Request Demo
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </AppBar>
  );
} 